import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { RefreshCw, Users, Clock, DollarSign, TrendingUp, CheckCircle, XCircle, Plus, Search, Pencil, Trash2 } from 'lucide-react';
import { searchResource, addResource, fetchRoles } from '@/lib/notion-client';
import { useProductionManagement } from '@/lib/production-management-context';
import { useSprints } from '@/lib/sprint-context';
import { toast } from '@/hooks/use-toast';
import { getTagColors } from '@/lib/tag-colors';
import { NotionIcon } from '@/components/icons/notion-icon';

interface Resource {
  id: string; // This will be the notionId
  name: string;
  email: string;
  role?: string[];
  department?: string[];
  weeklyCapacity?: number | string;
  hourlyRate?: number | string;
  active?: boolean;
}

export function ResourcesPage() {
  const { resources, loadingResources, errorResources, refreshResources } = useProductionManagement();
  const { sprintTasks, sprints } = useSprints();
  const [refreshing, setRefreshing] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  
  // Add Resource Dialog States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<'email' | 'details'>('email');
  const [searchEmail, setSearchEmail] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundResource, setFoundResource] = useState<any>(null);
  const [editableResource, setEditableResource] = useState<any>(null);
  const [adding, setAdding] = useState(false);
  const [showCreateButton, setShowCreateButton] = useState(false);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  
  // Edit Resource Dialog States
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resourceToEdit, setResourceToEdit] = useState<Resource | null>(null);
  const [editingResource, setEditingResource] = useState<any>(null);
  const [updating, setUpdating] = useState(false);
  
  // Delete Resource Dialog States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Create Contact Dialog States
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [createContactEmail, setCreateContactEmail] = useState('');
  const [newContact, setNewContact] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    weeklyCapacity: 40,
    hourlyRate: 0,
    active: true,
    role: '',
  });
  const [creating, setCreating] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const handleRefresh = async (force = false) => {
    try {
      setRefreshing(true);
      await refreshResources(force);
    } catch (err) {
      console.error('Error refreshing resources:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSearchResource = async () => {
    if (!searchEmail.trim()) {
      toast({
        title: 'Email required',
        description: 'Please enter an email address',
      });
      return;
    }

    try {
      setSearching(true);
      setShowCreateButton(false);
      const result = await searchResource(searchEmail);
      
      if (result && result.notionId) {
        setFoundResource(result);
        setEditableResource({
          ...result,
          weeklyCapacity: result.weeklyCapacity || 40,
          hourlyRate: result.hourlyRate || 0,
          active: result.activeResource !== undefined ? result.activeResource : true,
        });
        setDialogStep('details');
      } else {
        toast({
          title: 'Resource not found',
          description: 'No resource found with that email address',
        });
      }
    } catch (err: any) {
      // Check if it's a 404 error
      if (err.message && err.message.includes('404')) {
        setShowCreateButton(true);
        toast({
          title: 'Resource not found',
          description: 'No resource found with that email address. You can create a new contact.',
        });
      } else {
        toast({
          title: 'Search failed',
          description: err instanceof Error ? err.message : 'Failed to search for resource',
        });
      }
    } finally {
      setSearching(false);
    }
  };

  const handleAddResource = async () => {
    try {
      setAdding(true);
      
      const resourceToAdd = {
        notionId: foundResource.notionId,
        name: foundResource.name,
        email: foundResource.email,
        role: foundResource.roles || [],
        department: foundResource.department || [],
        weeklyCapacity: editableResource.weeklyCapacity,
        hourlyRate: editableResource.hourlyRate,
        active: editableResource.active,
        tasks: foundResource.tasks || '',
      };

      await addResource(resourceToAdd);

      // Refresh resources list
      await handleRefresh(true);

      toast({
        title: 'Resource added',
        description: `${resourceToAdd.name} has been added successfully`,
      });

      // Reset dialog
      setDialogOpen(false);
      setDialogStep('email');
      setSearchEmail('');
      setFoundResource(null);
      setEditableResource(null);
    } catch (err) {
      toast({
        title: 'Failed to add resource',
        description: err instanceof Error ? err.message : 'An error occurred',
      });
    } finally {
      setAdding(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogStep('email');
    setSearchEmail('');
    setFoundResource(null);
    setEditableResource(null);
    setSearching(false);
    setAdding(false);
    setShowCreateButton(false);
  };

  const handleOpenCreateContactDialog = () => {
    setCreateContactEmail(searchEmail);
    setNewContact({
      firstName: '',
      lastName: '',
      email: searchEmail,
      weeklyCapacity: 40,
      hourlyRate: 0,
      active: true,
      role: '',
    });
    setDialogOpen(false);
    setCreateContactOpen(true);
    setShowCreateButton(false);
    setRoleSearchQuery('');
    loadRoles();
  };

  const handleOpenEditDialog = (resource: Resource) => {
    setResourceToEdit(resource);
    setEditingResource({
      notionId: resource.id, // Use resource.id which is the notionId
      active: resource.active,
      weeklyCapacity: resource.weeklyCapacity,
      hourlyRate: resource.hourlyRate,
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setResourceToEdit(null);
    setEditingResource(null);
    setUpdating(false);
  };

  const handleUpdateResource = async () => {
    if (!editingResource || !resourceToEdit) return;

    try {
      setUpdating(true);

      const updateData = {
        notionId: editingResource.notionId,
        active: editingResource.active,
        weeklyCapacity: parseFloat(editingResource.weeklyCapacity) || 0,
        hourlyRate: parseFloat(editingResource.hourlyRate) || 0,
        action: 'EDIT',
      };

      await addResource(updateData); // Using same endpoint with EDIT flag

      toast({
        title: 'Resource updated',
        description: `${resourceToEdit.name} has been updated successfully`,
      });

      handleEditDialogClose();
      
      // Refresh data
      await handleRefresh(true);
    } catch (err) {
      toast({
        title: 'Failed to update resource',
        description: err instanceof Error ? err.message : 'An error occurred',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteResource = async (resource: Resource) => {
    setResourceToDelete(resource);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteResource = async () => {
    if (!resourceToDelete) return;

    try {
      setDeleting(true);
      
      const deleteData = {
        notionId: resourceToDelete.id,
        action: 'DELETE',
      };

      await addResource(deleteData);

      toast({
        title: 'Resource deleted',
        description: `${resourceToDelete.name} has been deleted successfully`,
      });

      setDeleteDialogOpen(false);
      setResourceToDelete(null);
      
      // Refresh data
      await handleRefresh(true);
    } catch (err) {
      toast({
        title: 'Failed to delete resource',
        description: err instanceof Error ? err.message : 'An error occurred',
      });
    } finally {
      setDeleting(false);
    }
  };

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const result = await fetchRoles();
      setRoles(result.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast({
        title: 'Failed to load roles',
        description: 'Could not fetch available roles',
      });
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.firstName || !newContact.lastName || !newContact.email) {
      toast({
        title: 'Required fields missing',
        description: 'Please fill in first name, last name, and email',
      });
      return;
    }

    try {
      setCreating(true);

      const contactData = {
        firstName: newContact.firstName,
        lastName: newContact.lastName,
        email: newContact.email,
        weeklyCapacity: parseFloat(newContact.weeklyCapacity) || 40,
        hourlyRate: parseFloat(newContact.hourlyRate) || 0,
        active: newContact.active,
        role: newContact.role,
        action: 'CREATE',
      };

      await addResource(contactData);

      toast({
        title: 'Contact created',
        description: `${newContact.firstName} ${newContact.lastName} has been created successfully`,
      });

      setCreateContactOpen(false);
      setNewContact({
        firstName: '',
        lastName: '',
        email: '',
        weeklyCapacity: 40,
        hourlyRate: 0,
        active: true,
        role: '',
      });
      setCreateContactEmail('');

      // Refresh resources list
      handleRefresh(true);
    } catch (err) {
      toast({
        title: 'Failed to create contact',
        description: err instanceof Error ? err.message : 'An error occurred',
      });
    } finally {
      setCreating(false);
    }
  };

  // Apply department filter first (affects stats)
  let baseFilteredResources = resources;
  if (selectedDepartments.length > 0) {
    baseFilteredResources = resources.filter(r => 
      Array.isArray(r.department) && 
      r.department.some(dept => selectedDepartments.includes(dept))
    );
  }
  
  // Then filter by active status for display
  const displayedResources = showInactive 
    ? baseFilteredResources 
    : baseFilteredResources.filter(r => r.active);

  // Calculate stats based on filtered resources (parse strings to numbers)
  const activeResources = baseFilteredResources.filter(r => r.active).length;
  const totalWeeklyCapacity = baseFilteredResources
    .filter(r => r.active)
    .reduce((sum, r) => {
      const capacity = typeof r.weeklyCapacity === 'string' 
        ? parseFloat(r.weeklyCapacity) 
        : r.weeklyCapacity;
      return sum + (capacity || 0);
    }, 0);
  const activeResourcesWithRate = baseFilteredResources.filter(r => {
    const rate = typeof r.hourlyRate === 'string' 
      ? parseFloat(r.hourlyRate) 
      : (r.hourlyRate || 0);
    return r.active && rate > 0;
  });
  const avgHourlyRate = activeResourcesWithRate.length > 0
    ? activeResourcesWithRate
        .reduce((sum, r) => {
          const rate = typeof r.hourlyRate === 'string' 
            ? parseFloat(r.hourlyRate) 
            : (r.hourlyRate || 0);
          return sum + rate;
        }, 0) / activeResourcesWithRate.length
    : 0;

  // Department counts (based on filtered resources)
  const deptCounts = baseFilteredResources.filter(r => r.active).reduce((acc, resource) => {
    // Ensure department is an array before iterating
    if (Array.isArray(resource.department)) {
      resource.department.forEach(dept => {
        acc[dept] = (acc[dept] || 0) + 1;
      });
    }
    return acc;
  }, {} as Record<string, number>);

  if (loadingResources && resources.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-3">
          <Spinner className="h-8 w-8 mx-auto" />
          <p className="text-sm text-muted-foreground">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Error Alert */}
      {errorResources && (
        <Alert variant="destructive">
          <AlertDescription>{errorResources}</AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Resources</h1>
          <p className="text-muted-foreground mt-1">
            Team capacity and resource management
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => setDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Active Only
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-2" />
                Show All
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRefresh(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {errorResources && (
        <Alert variant="destructive">
          <AlertDescription>{errorResources}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Resources</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeResources}</div>
            <p className="text-xs text-muted-foreground">
              {baseFilteredResources.length - activeResources} inactive
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Weekly Capacity</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(totalWeeklyCapacity / 8).toFixed(1)}d</div>
            <p className="text-xs text-muted-foreground mt-1">{totalWeeklyCapacity}h/week</p>
            <p className="text-xs text-muted-foreground">
              Across {activeResources} resources
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Hourly Rate</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${avgHourlyRate.toFixed(0)}</div>
            <p className="text-xs text-muted-foreground">
              Per hour average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Departments</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{Object.keys(deptCounts).length}</div>
            <p className="text-xs text-muted-foreground">
              {Object.entries(deptCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 2)
                .map(([dept]) => dept)
                .join(', ')}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Department Breakdown */}
      {Object.keys(deptCounts).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Department Distribution</CardTitle>
            <CardDescription>Active resources by department - Click to filter</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {Object.entries(deptCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([dept, count]) => (
                  <div 
                    key={dept} 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => {
                      setSelectedDepartments(prev => 
                        prev.includes(dept) 
                          ? prev.filter(d => d !== dept)
                          : [...prev, dept]
                      );
                    }}
                  >
                    <Badge 
                      className={`${getTagColors(dept).bg} ${getTagColors(dept).text} ${getTagColors(dept).border} border`}
                    >
                      {dept}
                    </Badge>
                    <span className="text-sm text-muted-foreground">{count}</span>
                  </div>
                ))}
              {selectedDepartments.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDepartments([]);
                  }}
                  className="h-6 px-2 text-xs"
                >
                  Clear filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resources Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {displayedResources.map((resource) => (
          <Card 
            key={resource.id} 
            className={!resource.active ? 'opacity-60' : ''}
          >
            <CardHeader>
              <div className="flex items-start gap-3">
                <Avatar>
                  <AvatarFallback>
                    {resource.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    {resource.name}
                    {resource.active ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                  </CardTitle>
                  <CardDescription className="truncate">
                    {resource.email}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Roles */}
              {resource.role && Array.isArray(resource.role) && resource.role.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Roles</p>
                  <div className="flex flex-wrap gap-1.5">
                    {resource.role.map((role, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Departments */}
              {resource.department && Array.isArray(resource.department) && resource.department.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Departments</p>
                  <div className="flex flex-wrap gap-1.5">
                    {resource.department.map((dept, idx) => (
                      <Badge 
                        key={idx} 
                        className={`text-xs ${getTagColors(dept).bg} ${getTagColors(dept).text} ${getTagColors(dept).border} border`}
                      >
                        {dept}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Capacity & Rate */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                <div>
                  <p className="text-xs text-muted-foreground">Weekly Capacity</p>
                  <p className="text-sm font-semibold">{typeof resource.weeklyCapacity === 'string' ? (parseFloat(resource.weeklyCapacity) / 8).toFixed(1) : ((resource.weeklyCapacity || 0) / 8).toFixed(1)}d</p>
                  <p className="text-xs text-muted-foreground">{resource.weeklyCapacity}h</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Hourly Rate</p>
                  <p className="text-sm font-semibold">
                    {(typeof resource.hourlyRate === 'number' ? resource.hourlyRate : parseFloat(String(resource.hourlyRate || 0))) > 0 
                      ? `$${resource.hourlyRate}` 
                      : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Utilization */}
              {(() => {
                // Calculate resource utilization
                const capacity = typeof resource.weeklyCapacity === 'string' 
                  ? parseFloat(resource.weeklyCapacity) 
                  : (resource.weeklyCapacity || 0);
                
                // Get current sprint tasks for this resource
                const currentSprint = sprints.find(s => 
                  s.status?.toLowerCase() === 'current' || 
                  s.status?.toLowerCase() === 'active' || 
                  s.status?.toLowerCase() === 'in progress'
                );
                
                const resourceTasks = sprintTasks.filter(task => {
                  const taskStatus = task.status?.toLowerCase() || '';
                  return task.contributors?.includes(resource.id) &&
                    (currentSprint ? task.sprints.includes(currentSprint.id) : true) &&
                    !taskStatus.includes('done') &&
                    !taskStatus.includes('archived') &&
                    !taskStatus.includes('completed');
                });
                
                const remainingHours = resourceTasks.reduce((sum, task) => {
                  const estimated = task.estimatedHours || 0;
                  const realised = task.realisedHours || 0;
                  return sum + Math.max(0, estimated - realised);
                }, 0);
                
                const utilization = capacity > 0 ? Math.round((remainingHours / capacity) * 100) : 0;
                
                if (resource.active && resourceTasks.length > 0) {
                  return (
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Utilization</span>
                        <span className={`text-xs font-semibold ${
                          utilization > 100 ? 'text-red-600' :
                          utilization >= 80 ? 'text-orange-600' :
                          'text-green-600'
                        }`}>
                          {utilization}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            utilization > 100 ? 'bg-red-500' :
                            utilization >= 80 ? 'bg-orange-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, utilization)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {remainingHours.toFixed(1)}h remaining
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Edit and Delete Buttons */}
              <div className="pt-2 border-t flex gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => window.open(`https://www.notion.so/${resource.id.replace(/-/g, '')}`, '_blank')}
                    >
                      <NotionIcon size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View in Notion</p>
                  </TooltipContent>
                </Tooltip>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1"
                  onClick={() => handleOpenEditDialog(resource)}
                >
                  <Pencil className="h-3 w-3 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDeleteResource(resource)}
                  className="flex-1 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3 w-3 mr-2" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {displayedResources.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              {showInactive ? 'No resources found' : 'No active resources found'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Add Resource Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-[500px]">
          {dialogStep === 'email' ? (
            <>
              <DialogHeader>
                <DialogTitle>Add Resource</DialogTitle>
                <DialogDescription>
                  Enter the email address to search for a resource
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !searching) {
                        handleSearchResource();
                      }
                    }}
                    disabled={searching}
                  />
                </div>
                {searching && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner className="h-4 w-4" />
                    <span>Searching for resource...</span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleDialogClose} disabled={searching}>
                  Cancel
                </Button>
                <Button onClick={handleSearchResource} disabled={searching || !searchEmail.trim()}>
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
                {showCreateButton && (
                  <Button onClick={handleOpenCreateContactDialog} variant="default">
                    Create Notion Contact
                  </Button>
                )}
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Resource Details</DialogTitle>
                <DialogDescription>
                  Review and edit resource information before adding
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Name and Email (Read-only) */}
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={foundResource?.name || ''} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={foundResource?.email || ''} disabled />
                </div>

                {/* Roles (Read-only) */}
                {foundResource?.roles && Array.isArray(foundResource.roles) && foundResource.roles.length > 0 && (
                  <div className="space-y-2">
                    <Label>Roles</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {foundResource.roles.map((role: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Departments (Read-only) */}
                {foundResource?.department && Array.isArray(foundResource.department) && foundResource.department.length > 0 && (
                  <div className="space-y-2">
                    <Label>Departments</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {foundResource.department.map((dept: string, idx: number) => (
                        <Badge 
                          key={idx} 
                          className={`text-xs ${getTagColors(dept).bg} ${getTagColors(dept).text} ${getTagColors(dept).border} border`}
                        >
                          {dept}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tasks (Read-only) */}
                {foundResource?.tasks && (
                  <div className="space-y-2">
                    <Label>Tasks</Label>
                    <Input value={foundResource.tasks} disabled />
                  </div>
                )}

                {/* Editable Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weeklyCapacity">Weekly Capacity (hours)</Label>
                    <Input
                      id="weeklyCapacity"
                      type="number"
                      min="0"
                      value={editableResource?.weeklyCapacity || 0}
                      onChange={(e) => setEditableResource({
                        ...editableResource,
                        weeklyCapacity: parseInt(e.target.value) || 0
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      min="0"
                      value={editableResource?.hourlyRate || 0}
                      onChange={(e) => setEditableResource({
                        ...editableResource,
                        hourlyRate: parseFloat(e.target.value) || 0
                      })}
                    />
                  </div>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Active Resource</Label>
                    <p className="text-xs text-muted-foreground">
                      Mark this resource as currently active
                    </p>
                  </div>
                  <Switch
                    checked={editableResource?.active || false}
                    onCheckedChange={(checked) => setEditableResource({
                      ...editableResource,
                      active: checked
                    })}
                  />
                </div>

                {adding && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Spinner className="h-4 w-4" />
                    <span>Adding resource...</span>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setDialogStep('email');
                    setFoundResource(null);
                    setEditableResource(null);
                  }}
                  disabled={adding}
                >
                  Back
                </Button>
                <Button onClick={handleAddResource} disabled={adding}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Resource
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Resource Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={handleEditDialogClose}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Resource</DialogTitle>
            <DialogDescription>
              Update resource information for {resourceToEdit?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name and Email (Read-only) */}
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={resourceToEdit?.name || ''} disabled />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={resourceToEdit?.email || ''} disabled />
            </div>

            {/* Roles (Read-only) */}
            {resourceToEdit?.role && Array.isArray(resourceToEdit.role) && resourceToEdit.role.length > 0 && (
              <div className="space-y-2">
                <Label>Roles</Label>
                <div className="flex flex-wrap gap-1.5">
                  {resourceToEdit.role.map((role, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Departments (Read-only) */}
            {resourceToEdit?.department && Array.isArray(resourceToEdit.department) && resourceToEdit.department.length > 0 && (
              <div className="space-y-2">
                <Label>Departments</Label>
                <div className="flex flex-wrap gap-1.5">
                  {resourceToEdit.department.map((dept, idx) => (
                    <Badge 
                      key={idx} 
                      className={`text-xs ${getTagColors(dept).bg} ${getTagColors(dept).text} ${getTagColors(dept).border} border`}
                    >
                      {dept}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Editable Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-weeklyCapacity">Weekly Capacity (hours)</Label>
                <Input
                  id="edit-weeklyCapacity"
                  type="number"
                  min="0"
                  value={editingResource?.weeklyCapacity || 0}
                  onChange={(e) => setEditingResource({
                    ...editingResource,
                    weeklyCapacity: e.target.value
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-hourlyRate">Hourly Rate ($)</Label>
                <Input
                  id="edit-hourlyRate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={editingResource?.hourlyRate || 0}
                  onChange={(e) => setEditingResource({
                    ...editingResource,
                    hourlyRate: e.target.value
                  })}
                />
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active Resource</Label>
                <p className="text-xs text-muted-foreground">
                  Mark this resource as currently active
                </p>
              </div>
              <Switch
                checked={editingResource?.active || false}
                onCheckedChange={(checked) => setEditingResource({
                  ...editingResource,
                  active: checked
                })}
              />
            </div>

            {updating && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" />
                <span>Updating resource...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleEditDialogClose} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleUpdateResource} disabled={updating}>
              <Pencil className="h-4 w-4 mr-2" />
              Update Resource
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Resource</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{resourceToDelete?.name}</strong>? This action cannot be undone and will permanently remove this resource from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteResource();
              }}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Resource
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Contact Dialog */}
      <Dialog open={createContactOpen} onOpenChange={setCreateContactOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Notion Contact</DialogTitle>
            <DialogDescription>
              Create a new contact in Notion for {createContactEmail}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={newContact.firstName}
                  onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                  placeholder="John"
                  disabled={creating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={newContact.lastName}
                  onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                  placeholder="Doe"
                  disabled={creating}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="createEmail">Email *</Label>
              <Input
                id="createEmail"
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                disabled={creating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="roles">Role</Label>
              <Input
                id="roleSearch"
                placeholder="Search roles..."
                value={roleSearchQuery}
                onChange={(e) => setRoleSearchQuery(e.target.value)}
                disabled={creating || loadingRoles}
              />
              <div className="border rounded-md max-h-[200px] overflow-y-auto">
                {loadingRoles ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">Loading roles...</div>
                ) : roles.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No roles available</div>
                ) : (
                  <div className="p-2 space-y-1">
                    {roles
                      .filter((role: any) => 
                        role.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
                      )
                      .map((role: any) => (
                        <label
                          key={role.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-accent cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="contactRole"
                            checked={newContact.role === role.id}
                            onChange={() => {
                              setNewContact({ 
                                ...newContact, 
                                role: role.id 
                              });
                            }}
                            disabled={creating}
                            className="rounded"
                          />
                          <span className="text-sm">{role.name}</span>
                        </label>
                      ))}
                    {roles.filter((role: any) => 
                      role.name.toLowerCase().includes(roleSearchQuery.toLowerCase())
                    ).length === 0 && (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        No roles match your search
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {newContact.role ? '1 role selected' : 'No role selected'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="createWeeklyCapacity">Weekly Capacity (hours)</Label>
                <Input
                  id="createWeeklyCapacity"
                  type="number"
                  min="0"
                  value={newContact.weeklyCapacity}
                  onChange={(e) => setNewContact({ ...newContact, weeklyCapacity: e.target.value })}
                  disabled={creating}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="createHourlyRate">Hourly Rate ($)</Label>
                <Input
                  id="createHourlyRate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newContact.hourlyRate}
                  onChange={(e) => setNewContact({ ...newContact, hourlyRate: e.target.value })}
                  disabled={creating}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Active Contact</Label>
                <p className="text-xs text-muted-foreground">
                  Mark this contact as currently active
                </p>
              </div>
              <Switch
                checked={newContact.active}
                onCheckedChange={(checked) => setNewContact({ ...newContact, active: checked })}
                disabled={creating}
              />
            </div>

            {creating && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" />
                <span>Creating contact...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateContactOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreateContact} disabled={creating}>
              <Plus className="h-4 w-4 mr-2" />
              Create Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
